/**
 * Mortgage Tracker page — Phase 5 full implementation, Phase 1 shell.
 * Even in Phase 1 this fetches real data from the worker (which reads Forkcast KV).
 */

import { useEffect, useState } from 'react';
import { createChart, type IChartApi } from 'lightweight-charts';
import { mortgageApi, type MortgageData } from '../api.ts';
import { formatGbp, formatPercent, formatMonthYear } from '../lib/format.ts';

export default function Mortgage() {
  const [data, setData] = useState<MortgageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    mortgageApi
      .get()
      .then(setData)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} />;
  if (!data) return null;

  const { mortgages, summary } = data;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Mortgage Tracker</h1>
        <a
          href="https://clankfield-blarr.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-brand-400 hover:text-brand-300 transition-colors"
        >
          Edit in Forkcast ↗
        </a>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MiniCard label="Property Value" value={formatGbp(summary.property_value_gbp)} />
        <MiniCard label="Outstanding" value={formatGbp(summary.total_balance_gbp)} />
        <MiniCard label="Equity" value={formatGbp(summary.equity_gbp)} />
        <MiniCard label="LTV" value={formatPercent(summary.ltv_percent)} />
      </div>

      {/* Per-mortgage cards */}
      <div className="grid lg:grid-cols-2 gap-6">
        {mortgages.map((m) => (
          <MortgageCard key={m.id} mortgage={m} />
        ))}
      </div>

      {/* Amortisation chart placeholder */}
      <section className="bg-gray-900 border border-gray-800 rounded-xl p-6">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
          Amortisation Chart
        </h2>
        <p className="text-sm text-gray-600">Phase 5 — amortisation and equity growth charts.</p>
      </section>

      {/* Overpayment tool placeholder */}
      <section className="bg-gray-900 border border-gray-800 rounded-xl p-6">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
          Overpayment Scenario
        </h2>
        <p className="text-sm text-gray-600">Phase 5 — client-side overpayment scenario tool.</p>
      </section>
    </div>
  );
}

function MortgageCard({ mortgage: m }: { mortgage: MortgageData['mortgages'][number] }) {
  const rateEndFormatted = formatMonthYear(m.rateEndDate + '-01');
  const elapsed = monthsElapsed(m.startDate);
  const remaining = Math.max(0, m.termMonths - elapsed);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-white">{m.label}</h3>
          {m.lender && <p className="text-sm text-gray-500 mt-0.5">{m.lender}</p>}
        </div>
        <span
          className={[
            'text-xs px-2 py-1 rounded font-medium',
            m.rateType === 'fixed'
              ? 'bg-blue-900/60 text-blue-300'
              : 'bg-amber-900/60 text-amber-300',
          ].join(' ')}
        >
          {m.rateType}
        </span>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <DataItem label="Balance" value={formatGbp(m.balance, true)} />
        <DataItem label="Monthly payment" value={formatGbp(m.monthlyPayment, true)} />
        <DataItem label="Rate" value={`${(m.rate * 100).toFixed(2)}%`} />
        <DataItem label="Rate ends" value={rateEndFormatted} />
        <DataItem label="Term" value={`${m.termMonths} months`} />
        <DataItem label="Remaining" value={`~${remaining} months`} />
      </dl>
    </div>
  );
}

function DataItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-mono text-white font-medium tabular-nums">{value}</dd>
    </div>
  );
}

function MiniCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-lg font-bold text-white font-mono tabular-nums">{value}</p>
    </div>
  );
}

function monthsElapsed(startDate: string): number {
  const [year, month] = startDate.split('-').map(Number);
  const start = new Date(year, month - 1);
  const now = new Date();
  return (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="bg-red-950/40 border border-red-900/50 rounded-xl p-6 text-red-400">
      Failed to load mortgage data: {message}
    </div>
  );
}

// createChart and IChartApi will be used in Phase 5 for amortisation charts
void (createChart as unknown);
void (0 as unknown as IChartApi);
export type { IChartApi };
